// Path D — pure helper that turns generateMatchesForRound's `unplacedReasons`
// array into a multi-line, human-readable string suitable for sonner's
// `toast.success(message, { description })`. Extracted from
// LeagueRoundsManager.tsx so the formatting logic (reason mapping +
// "...and X more" cap) can be unit-tested without DOM/React harness.
//
// Each item formats as:
//   "<home> vs <away> (<division>): <reason>"
//
// The reason field gets a friendly mapping for the common sentinel
// `"no_available_slot"` ("Ingen ledig tid i Speltider"); anything else
// is treated as a technical error message (autoSchedule exception).
//
// i18n keys used (all in the `admin` namespace, under `leagues.rounds.*`):
//   - unplacedReasonsItem
//   - unplacedReasonNoSlot
//   - unplacedReasonError
//   - unplacedReasonsMore

export type UnplacedReason = {
  matchId: string;
  reason: string;
  homeTeamName?: string | null;
  awayTeamName?: string | null;
  divisionName?: string | null;
};

/**
 * Minimal i18n-function shape — covers both `useTranslation`'s `t` and the
 * test-friendly identity mock. We intentionally keep the type narrow so
 * tests don't need to import `i18next`'s full type machinery.
 */
type TranslateFn = (
  key: string,
  optionsOrDefault?: string | (Record<string, unknown> & { defaultValue?: string }),
) => string;

export interface BuildUnplacedReasonsDescriptionArgs {
  reasons: UnplacedReason[];
  t: TranslateFn;
  /** Cap on the number of items shown before "...and X more" suffix. Default 5. */
  maxItems?: number;
}

const SENTINEL_NO_SLOT = "no_available_slot";

function formatReason(reason: string, t: TranslateFn): string {
  if (reason === SENTINEL_NO_SLOT) {
    return t("leagues.rounds.unplacedReasonNoSlot", {
      defaultValue: "Ingen ledig tid i Speltider",
    });
  }
  return t("leagues.rounds.unplacedReasonError", {
    message: reason,
    defaultValue: "Tekniskt fel: {{message}}",
  });
}

/**
 * Render the unplaced-reasons array as a newline-joined string. Returns
 * an empty string when `reasons` is empty so the caller can pass it
 * straight into `toast.success(..., { description })` without branching.
 */
export function buildUnplacedReasonsDescription(
  args: BuildUnplacedReasonsDescriptionArgs,
): string {
  const { reasons, t, maxItems = 5 } = args;
  if (reasons.length === 0) return "";

  const lines: string[] = reasons.slice(0, maxItems).map((r) =>
    t("leagues.rounds.unplacedReasonsItem", {
      home: r.homeTeamName ?? "?",
      away: r.awayTeamName ?? "?",
      division: r.divisionName ?? "?",
      reason: formatReason(r.reason, t),
      defaultValue: "{{home}} vs {{away}} ({{division}}): {{reason}}",
    }),
  );

  const more = reasons.length - maxItems;
  if (more > 0) {
    lines.push(
      t("leagues.rounds.unplacedReasonsMore", {
        n: more,
        defaultValue: "...och {{n}} till",
      }),
    );
  }

  return lines.join("\n");
}
